// Kitchen flow pilot, Batch 5 lane purchasing — pure rules behind the
// purchasing fixes. Mirrors backend handlers/stock_purchasing_pilot.go where a
// rule exists on both sides. Pinned by purchasingPilotLogic.test.ts.

const EPS = 1e-9

function round(n: number, places: number): number {
  const f = 10 ** places
  return Math.round(n * f) / f
}

function trimNum(n: number): string {
  return String(round(n, 3))
}

// ── Receive: price per line ──────────────────────────────────────────────

/** Mirror of receiptPriceAboveApproved: above the approved price by more than
 *  1 % (never less than one paisa). Cheaper never needs a reason. */
export function receiptPriceAboveApproved(approved: number, received: number): boolean {
  const tol = Math.max(approved * 0.01, 0.01)
  return received > approved + tol + EPS
}

/** A typed price: a number ≥ 0, or null when blank / not a number / negative. */
export function parsePrice(text: string): number | null {
  const t = text.trim()
  if (!t) return null
  const n = Number(t)
  return Number.isFinite(n) && n >= 0 ? n : null
}

export type ReceivePriceProblem = 'needs_price' | 'bad_price' | 'needs_price_reason' | null

/** What the receive line's price still needs (kitchen flow). A line that
 *  received nothing needs nothing. A line whose order had no price must get
 *  one or be marked "no price on this delivery"; a price above the approved
 *  one needs a reason. */
export function receivePriceProblem(l: {
  qty: number
  unitCost: string
  orderCost: number | null
  noPrice: boolean
  priceReason: string
}): ReceivePriceProblem {
  if (!(l.qty > 0)) return null
  const text = l.unitCost.trim()
  if (!text) return l.noPrice ? null : 'needs_price'
  const price = parsePrice(text)
  if (price == null) return 'bad_price'
  if (l.orderCost != null && receiptPriceAboveApproved(l.orderCost, price) && !l.priceReason.trim()) return 'needs_price_reason'
  return null
}

/** True when the typed price differs from the order's (shown as "Price changed"). */
export function receivePriceChanged(unitCost: string, orderCost: number | null): boolean {
  const p = parsePrice(unitCost)
  if (p == null) return false
  if (orderCost == null) return false
  return Math.abs(p - orderCost) > 0.0001
}

// ── Returns ──────────────────────────────────────────────────────────────

/** The credit the vendor owes for a return: qty × the line's unit cost,
 *  rounded to paisa. Null when either is unknown. */
export function returnCreditAmount(qty: number, unitCost: number | null | undefined): number | null {
  if (!(qty > 0) || unitCost == null || !(unitCost >= 0)) return null
  return round(qty * unitCost, 2)
}

// ── Packs ────────────────────────────────────────────────────────────────

export interface Pack {
  label: string
  qty: number
}

/** Mirror of resolvePOPack: the vendor's catalogue pack first, else the item's
 *  own buying unit when it is more than one base unit. */
export function resolvePack(p: {
  vendorLabel?: string | null
  vendorQty?: number | null
  itemLabel?: string | null
  itemFactor?: number | null
}): Pack | null {
  if (p.vendorQty != null && p.vendorQty > EPS) {
    return { label: p.vendorLabel?.trim() || 'pack', qty: p.vendorQty }
  }
  if (p.itemFactor != null && p.itemFactor > 1 + EPS && p.itemLabel?.trim()) {
    return { label: p.itemLabel.trim(), qty: p.itemFactor }
  }
  return null
}

/** Mirror of roundUpToPacks: 30 kg of a 25 kg bag → 50 kg. */
export function roundUpToPacks(qty: number, packQty: number | null | undefined): number {
  if (!packQty || packQty <= EPS || qty <= EPS) return qty
  return round(Math.ceil(qty / packQty - 1e-6) * packQty, 3)
}

/** A quantity the order line stores (base units) from what was typed. */
export function toBaseQty(typed: number, inPacks: boolean, pack: Pack | null): number {
  return inPacks && pack ? round(typed * pack.qty, 5) : typed
}

/** What to show in the quantity box for a base quantity. */
export function fromBaseQty(base: number, inPacks: boolean, pack: Pack | null): number {
  return inPacks && pack ? round(base / pack.qty, 3) : base
}

/** Price per base unit from a typed price per pack (or per unit). */
export function toBaseCost(typed: number, inPacks: boolean, pack: Pack | null): number {
  return inPacks && pack ? round(typed / pack.qty, 5) : typed
}

/** Price per pack (or per unit) to show for a base price. */
export function fromBaseCost(base: number, inPacks: boolean, pack: Pack | null): number {
  return inPacks && pack ? round(base * pack.qty, 2) : base
}

function packCountText(packs: number, label: string): string {
  const n = trimNum(packs)
  if (!/^\p{L}+$/u.test(label)) return `${n} × ${label}`
  let word = label.toLowerCase()
  if (Math.abs(packs - 1) > EPS && !word.endsWith('s')) word += 's'
  return `${n} ${word}`
}

/** Mirror of poLineQtyText: "2 bags (50 kg)" for a whole number of packs,
 *  else "50 kg". */
export function poLineQtyText(qty: number, unit: string, pack: Pack | null | undefined): string {
  const base = `${trimNum(qty)} ${unit}`.trim()
  if (!pack || pack.qty <= EPS) return base
  const packs = qty / pack.qty
  if (Math.abs(packs - Math.round(packs)) > 1e-6 || Math.round(packs) < 1) return base
  return `${packCountText(Math.round(packs), pack.label)} (${base})`
}

// ── Item search (PO item picker) ─────────────────────────────────────────

/** Items whose name or code contains the query (case-insensitive), names
 *  that start with it first, at most `limit`. */
export function searchItems<T extends { name: string; code?: string | null; barcode?: string | null }>(
  items: T[],
  query: string,
  limit = 20,
): T[] {
  const q = query.trim().toLowerCase()
  if (!q) return []
  const hits = items.filter((i) =>
    i.name.toLowerCase().includes(q) || (i.code ?? '').toLowerCase().includes(q) || (i.barcode ?? '').toLowerCase() === q)
  hits.sort((a, b) => {
    const as = a.name.toLowerCase().startsWith(q) ? 0 : 1
    const bs = b.name.toLowerCase().startsWith(q) ? 0 : 1
    return as - bs || a.name.localeCompare(b.name)
  })
  return hits.slice(0, limit)
}

/** The price a PO line starts at for an item: the vendor's catalogue price,
 *  else the item's last paid / average / default cost. */
export function startingCost(p: {
  catalogue?: number | null
  lastPurchase?: number | null
  wac?: number | null
  defaultCost?: number | null
}): number | undefined {
  if (p.catalogue != null && p.catalogue >= 0) return p.catalogue
  if (p.lastPurchase != null && p.lastPurchase > 0) return p.lastPurchase
  if (p.wac != null && p.wac > 0) return p.wac
  if (p.defaultCost != null && p.defaultCost > 0) return p.defaultCost
  return undefined
}

// ── Approvals ────────────────────────────────────────────────────────────

/** Mirror of parsePOSelfApproval: missing or not a boolean means on. */
export function parseSelfApproval(setting: unknown): boolean {
  return typeof setting === 'boolean' ? setting : true
}

/** May I approve THIS order? Mirror of poSelfApprovalRefused on top of the
 *  role rule (canApprovePO). */
export function canApproveThisPO(p: {
  canApprove: boolean
  role: string
  createdBy?: string | null
  myId: string
  selfApproval: boolean
}): boolean {
  if (!p.canApprove) return false
  if (p.role === 'admin' || p.selfApproval) return true
  return !(p.createdBy && p.createdBy === p.myId)
}

// ── Delivery-slip photos ─────────────────────────────────────────────────

/** Mirror of poPhotoDeleteAllowed. */
export function canDeletePoPhoto(role: string, poStatus: string | null | undefined): boolean {
  if (role === 'admin') return true
  return poStatus !== 'received' && poStatus !== 'cancelled'
}

// ── Draft rows that survive a refetch ────────────────────────────────────

/** Re-seed editable rows from fresh server data without wiping the ones
 *  being edited: a row whose draft differs from what the server sent last
 *  time keeps the draft; every other row takes the fresh value. */
export function mergeDrafts<D>(
  current: Record<string, D>,
  previousServer: Record<string, D>,
  freshServer: Record<string, D>,
  same: (a: D, b: D) => boolean,
): Record<string, D> {
  const out: Record<string, D> = {}
  for (const [id, fresh] of Object.entries(freshServer)) {
    const cur = current[id]
    const prev = previousServer[id]
    const editing = cur !== undefined && prev !== undefined && !same(cur, prev)
    out[id] = editing ? cur : fresh
  }
  return out
}
